<template>
  <AppDialog
    :disable-confirm-button="disableConfirmButton"
    :loading-confirm-button="loading"
    :show="showModal"
    :title="getModalTitle"
    :type="DIALOG_TYPES.INFO"
    hide-hero
    @on-close="closeModal"
    @on-confirm="onConfirm"
  >
    <template #button-cancel>
      <AppButton v-show="!isScimStep" :loading="loading" type="ghost-next" @click="closeModal">
        {{ $t('action.cancel') }}
      </AppButton>
    </template>
    <template #confirm-btn-text>{{ getConfirmBtnText }}</template>
    <div class="cm-Wrapper">
      <template v-if="isStartStep">
        <FormFieldNext :label="$t('integration_settings.table_header_protocol')">
          <AppSelect
            v-model="protocol"
            :disabled-items="getDisabledProtocolItems"
            :offset="[0, 0]"
            :options="getProtocolOptions"
          >
            <template #button-content="{ option }">
              <span v-if="!option">
                {{ $t('sso.select_protocol') }}
              </span>
            </template>
          </AppSelect>
        </FormFieldNext>

        <FormFieldNext :label="$t('integration_settings.table_header_provider')" required>
          <AppSelect v-model="provider" :offset="[0, 0]" :options="getProviders()">
            <template #button-content="{ option }">
              <span v-if="!option" class="oboard-truncated-text">
                {{ $t('sso.select_provider') }}
              </span>
            </template>
          </AppSelect>
        </FormFieldNext>

        <FormFieldNext :label="$t('integration_settings.connection_name')" required>
          <AppInput
            v-model="connectionName"
            :max-length="SSO_NAME_MAX_LENGTH"
            :placeholder="$t('integration_settings.enter_connection_name')"
            size="xlg"
            style-type="primary"
          />
        </FormFieldNext>

        <div class="cm-ConnectionGuideWrapper">
          <span class="cm-DocDescription">
            {{ $t('integration_settings.check_documentation') }}
          </span>

          <AppHrefLink
            href="https://oboard.atlassian.net/wiki/spaces/OBPFJ/pages/1678245891/Single+Sign+On+SSO+Provisioning"
            prepend-icon="guide"
          >
            {{ $t('connections.guide.link_text') }}
          </AppHrefLink>
        </div>
      </template>
      <template v-else-if="isSamlStep">
        <DomainsList ref="domains-list" v-model="connectionDomains" :loading="loading" />
      </template>
      <template v-else-if="isScimStep">
        <AppInfoMessage :type="INFO_MESSAGE_TYPES.WARNING">
          {{ t('integration_settings.authorization_token_not_visible_again') }}
        </AppInfoMessage>

        <FormFieldNext :label="$t('sso.provisioning_url')">
          <AppInput
            :model-value="provisioningData.url"
            copyable
            readonly
            size="xlg"
            style-type="primary"
          />
        </FormFieldNext>

        <FormFieldNext :label="$t('sso.auth_token')">
          <AppInput
            :model-value="provisioningData.token"
            copyable
            readonly
            size="xlg"
            style-type="primary"
          />
        </FormFieldNext>
      </template>
    </div>
  </AppDialog>
</template>

<script setup>
import { isNull, cloneDeep, isEmpty, uniqBy } from 'lodash'
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import SSOApiHandler from '@/api/sso'
import { DIALOG_TYPES } from '@/utils/components-configurations/app-dialog'
import { INFO_MESSAGE_TYPES } from '@/utils/components-configurations/app-info-message'
import { SAML_DOMAIN_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { isStringEmpty } from '@/utils/general'
import { showNotify } from '@/utils/notify'
import {
  CONNECTIONS_PROTOCOLS,
  createDefaultDomainModel,
  getProviders,
  isSaml,
  isSCIM,
  SAML_ID,
  SAML_TITLE,
  SCIM_ID,
  SCIM_TITLE,
  SSO_NAME_MAX_LENGTH
} from '@/utils/sso-connection-settings'

import AppDialog from '@/components/AppDialog'
import AppHrefLink from '@/components/AppHrefLink'
import FormFieldNext from '@/components/form/FormFieldNext'
import DomainsList from '@/components/integrations/sso/DomainsList'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppInfoMessage from '@/components/ui/AppInfoMessage'
import AppInput from '@/components/ui/AppInput/AppInput'
import AppSelect from '@/components/ui/AppSelect/AppSelect'

defineOptions({
  name: 'CreateSSOConnectionModal'
})

const emit = defineEmits({
  created: null,
  'setup-certificate': null
})

const props = defineProps({
  exceededLimits: {
    type: Array,
    default: () => []
  }
})

const isLimitExceeded = protocol => props.exceededLimits.includes(protocol)

const getDisabledProtocolItems = computed(() => {
  return CONNECTIONS_PROTOCOLS.filter(isLimitExceeded)
})

const loading = ref(false)
const showModal = defineModel('show', {
  type: Boolean
})

watch(
  () => showModal.value,
  newValue => {
    if (newValue) {
      const availableProtocols = CONNECTIONS_PROTOCOLS.filter(p => !isLimitExceeded(p))
      protocol.value = availableProtocols[0] || null
    }
  }
)

const DEFAULT_DOMAINS = [
  {
    ...createDefaultDomainModel()
  }
]

const closeModal = () => {
  showModal.value = false
  nextTick(() => {
    step.value = START_STEP
    connectionName.value = ''
    provider.value = null
    connectionDomains.value = cloneDeep(DEFAULT_DOMAINS)
  })
}

const START_STEP = 'START_STEP'
const SAML_STEP = 'SAML_STEP'
const SCIM_STEP = 'SCIM_STEP'

const protocol = ref(SAML_ID)

const step = ref(START_STEP)

const provider = ref(null)

const connectionDomains = ref(cloneDeep(DEFAULT_DOMAINS))

const getProtocolOptions = computed(() => {
  const getLabel = (value, label) => {
    const isOverLimited = isLimitExceeded(value)
    const suffix = isOverLimited ? ` ${t('integration_settings.connections_over_limit')}` : ''
    return label + suffix
  }

  return [
    {
      label: getLabel(SAML_ID, SAML_TITLE),
      value: SAML_ID
    },
    {
      label: getLabel(SCIM_ID, SCIM_TITLE),
      value: SCIM_ID
    }
  ]
})

const connectionName = ref('')

const { t } = useI18n()

const getModalTitle = computed(() => {
  if (isSamlStep.value) {
    return SAML_TITLE
  }

  if (isScimStep.value) {
    return SCIM_TITLE
  }

  return t('integration_settings.connection')
})

const provisioningData = ref({
  url: '',
  token: ''
})

const createScimConnection = async () => {
  const api = new SSOApiHandler()
  loading.value = true
  try {
    provisioningData.value = await api.createScimConnection({
      name: connectionName.value,
      provider: provider.value
    })
    step.value = SCIM_STEP
    emit('created')
  } catch (error) {
    handleError({ error })
  } finally {
    loading.value = false
  }
}

const isStartStep = computed(() => step.value === START_STEP)
const isSamlStep = computed(() => step.value === SAML_STEP)
const isScimStep = computed(() => step.value === SCIM_STEP)
const isSamlSelected = computed(() => isSaml(protocol.value))
const isScimSelected = computed(() => isSCIM(protocol.value))

const runInnerDomainsValidation = () => {
  connectionDomains.value.forEach(domain => {
    domain[SAML_DOMAIN_ENTITY_KEYS.DOMAIN_NAME] = domain[SAML_DOMAIN_ENTITY_KEYS.DOMAIN_NAME]
      .trim()
      .toLowerCase()
  })

  // uniq by name but skip empty strings
  connectionDomains.value = uniqBy(
    connectionDomains.value,
    item => item[SAML_DOMAIN_ENTITY_KEYS.DOMAIN_NAME] || Symbol()
  )

  const regex = new RegExp(
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/
  )

  connectionDomains.value.forEach(domain => {
    domain[SAML_DOMAIN_ENTITY_KEYS.IS_VALID] = regex.test(
      domain[SAML_DOMAIN_ENTITY_KEYS.DOMAIN_NAME]
    )
  })
}

const isSomeDomainFailsValidation = computed(() => {
  return connectionDomains.value.some(domain => !domain[SAML_DOMAIN_ENTITY_KEYS.IS_VALID])
})

const isSomeDomainNotUnique = computed(() => {
  return connectionDomains.value.some(domain => !domain[SAML_DOMAIN_ENTITY_KEYS.IS_UNIQUE])
})

const validateDomainsUniqueness = async () => {
  const api = new SSOApiHandler()

  loading.value = true
  try {
    const { invalidDomains } = await api.validateDomainsAvailabilityForSaml({
      domains: connectionDomains.value.map(d => d[SAML_DOMAIN_ENTITY_KEYS.DOMAIN_NAME])
    })

    connectionDomains.value.forEach(domain => {
      domain[SAML_DOMAIN_ENTITY_KEYS.IS_UNIQUE] = !invalidDomains.includes(
        domain[SAML_DOMAIN_ENTITY_KEYS.DOMAIN_NAME]
      )
    })
  } catch (error) {
    handleError({ error })
    throw error
  } finally {
    loading.value = false
  }
}

const domainsListReference = useTemplateRef('domains-list')

const onConfirm = async () => {
  if (isScimStep.value) {
    closeModal()
    return
  }

  if (isSamlStep.value) {
    runInnerDomainsValidation()

    if (isSomeDomainFailsValidation.value) {
      domainsListReference.value.moveToFirstInvalidDomain()
      return
    }

    await validateDomainsUniqueness()

    if (isSomeDomainNotUnique.value) {
      domainsListReference.value.moveToFirstInvalidDomain()
      return
    }

    await createSamlConnection()
    return
  }

  if (isSamlSelected.value && isStartStep.value) {
    step.value = SAML_STEP
    return
  }

  if (isScimSelected.value && isStartStep.value) {
    await createScimConnection()
  }
}

const disableConfirmButton = computed(() => {
  if (isSamlStep.value) {
    return isEmpty(connectionDomains.value)
  }

  return isStringEmpty(connectionName.value) || isNull(provider.value)
})

const createSamlConnection = async () => {
  const api = new SSOApiHandler()
  loading.value = true
  try {
    const connection = await api.createSamlConnection({
      name: connectionName.value,
      provider: provider.value,
      domains: connectionDomains.value.map(d => d[SAML_DOMAIN_ENTITY_KEYS.DOMAIN_NAME])
    })
    showNotify()
    emit('created')
    emit('setup-certificate', connection)
    closeModal()
  } catch (error) {
    handleError({ error })
  } finally {
    loading.value = false
  }
}

const getConfirmBtnText = computed(() => {
  if (isScimStep.value) {
    return t('objective.complete')
  }

  if (isSamlStep.value || isSCIM(protocol.value)) {
    return t('action.create_connection')
  }

  if (isSaml(protocol.value)) {
    return t('action.next_step')
  }

  return t('objective.complete')
})
</script>

<style lang="scss" scoped>
.cm-Wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.cm-ConnectionGuideWrapper {
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  border-radius: $border-radius-md-next;
  background: $grey-3-next;
}

.cm-DocDescription {
  color: $dark-1;
  font-weight: fw('semi-bold');
}
</style>
